import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtDMY } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CalendarRange, Loader2, Pencil, Trash2, Plus, FileInput } from "lucide-react";

export const LEAVE_TYPES = ["Annual leave", "Sick leave", "Bereavement leave", "Family violence leave", "Parental leave", "Unpaid leave",
  "Time off in lieu", "Alternative holiday", "Training / course", "Jury service", "ACC / injury", "Not rostered"];

const EMPTY = { start_date: "", end_date: "", label: "Annual leave", half_day: false, notes: "", request_id: null };

function LeaveForm({ cleanerId, initial, requests, onDone }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [f, setF] = useState(initial || EMPTY);
  useEffect(() => setF(initial || EMPTY), [initial]);
  const single = f.start_date && f.start_date === f.end_date;
  const calDays = f.start_date && f.end_date && f.end_date >= f.start_date ? Math.round((new Date(f.end_date) - new Date(f.start_date)) / 86400000) + 1 : 0;
  const { data: preview } = useQuery({
    queryKey: ["leave-preview", cleanerId, f.start_date, f.end_date],
    queryFn: async () => (await api.get(`/cleaners/${cleanerId}/leave/preview?start=${f.start_date}&end=${f.end_date}`)).data,
    enabled: calDays > 0,
  });
  const leaveDays = preview ? (f.half_day && single && preview.leave_days ? 0.5 : preview.leave_days) : null;
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["cleaner-leave", cleanerId] });
    qc.invalidateQueries({ queryKey: ["cleaners"] });
    qc.invalidateQueries({ queryKey: ["timesheets"] });
    qc.invalidateQueries({ queryKey: ["roster-day-labels"] });
  };
  const save = useMutation({
    mutationFn: () => (f.id ? api.put(`/cleaners/${cleanerId}/leave/${f.id}`, f) : api.post(`/cleaners/${cleanerId}/leave`, f)),
    onSuccess: ({ data }) => {
      toast.success(t("{{label}} saved — {{count}} rostered day(s) on the timesheet", { label: t(data.label), count: data.rostered_days }));
      invalidate(); onDone();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const fill = (r) => setF({ ...f, start_date: r.start_date, end_date: r.end_date, label: LEAVE_TYPES.includes(r.leave_type) ? r.leave_type : f.label,
    half_day: !!r.half_day, notes: (r.reason || "").slice(0, 500), request_id: r.id });
  const valid = f.start_date && f.end_date && f.end_date >= f.start_date && f.label;
  return (
    <div className="space-y-3 rounded-md border bg-secondary/30 p-3" data-testid="leave-form">
      {requests?.length > 0 && !f.id && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <FileInput className="h-3.5 w-3.5 text-muted-foreground" /> <span className="text-muted-foreground">{t("Fill from approved application:")}</span>
          {requests.map((r) => (
            <button key={r.id} type="button" onClick={() => fill(r)} data-testid={`leave-fill-${r.id}`}
              className={`rounded-full border px-2 py-0.5 ${f.request_id === r.id ? "border-indigo-600 bg-indigo-600 text-white" : "bg-card hover:bg-secondary"}`}>
              {t(r.leave_type)} · {fmtDMY(r.start_date)}{r.end_date !== r.start_date ? ` – ${fmtDMY(r.end_date)}` : ""}
            </button>
          ))}
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-3">
        <div className="space-y-1"><Label className="text-xs">{t("From")}</Label>
          <Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value, end_date: f.end_date && f.end_date < e.target.value ? e.target.value : f.end_date })} className="h-8" data-testid="leave-start" /></div>
        <div className="space-y-1"><Label className="text-xs">{t("To")}</Label>
          <Input type="date" value={f.end_date} min={f.start_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className="h-8" data-testid="leave-end" /></div>
        <div className="space-y-1"><Label className="text-xs">{t("Reason")}</Label>
          <select value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} className="h-8 w-full rounded-md border bg-background px-2 text-sm" data-testid="leave-label">
            {LEAVE_TYPES.map((l) => <option key={l} value={l}>{t(l)}</option>)}
          </select></div>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        {single && (
          <label className="flex items-center gap-1.5"><Switch checked={f.half_day} onCheckedChange={(v) => setF({ ...f, half_day: v })} data-testid="leave-half-day" /> {t("Half day")}</label>
        )}
        {calDays > 0 && (
          <span className="flex flex-wrap items-center gap-2" data-testid="leave-cal-days">
            {leaveDays !== null && (
              <span className="rounded-full bg-indigo-100 px-2 py-0.5 font-semibold text-indigo-800" data-testid="leave-days-preview">
                {t("{{count}} leave day(s)", { count: leaveDays })}
              </span>
            )}
            <span className="text-muted-foreground">
              {preview && (preview.days_off > 0 || preview.public_holidays > 0)
                ? t("{{count}} calendar day(s) — {{off}} normal day(s) off and {{ph}} public holiday(s) are not counted as leave", { count: calDays, off: preview.days_off, ph: preview.public_holidays })
                : t("{{count}} calendar day(s) — only rostered days are labelled; rostered days off stay as they are", { count: calDays })}
            </span>
          </span>
        )}
      </div>
      <Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder={t("Notes (optional)")} className="h-8" data-testid="leave-notes" />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onDone} data-testid="leave-cancel">{t("Cancel")}</Button>
        <Button size="sm" disabled={!valid || save.isPending} onClick={() => save.mutate()} className="gap-1" data-testid="leave-save">
          {save.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {f.id ? t("Save changes") : t("Add leave")}
        </Button>
      </div>
    </div>
  );
}

export function LeaveDialog({ cleaner, onClose }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null | "new" | period
  const { data } = useQuery({
    queryKey: ["cleaner-leave", cleaner?.id],
    queryFn: async () => (await api.get(`/cleaners/${cleaner.id}/leave`)).data,
    enabled: !!cleaner,
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/cleaners/${cleaner.id}/leave/${id}`),
    onSuccess: () => {
      toast.success(t("Leave removed — timesheet days cleared"));
      qc.invalidateQueries({ queryKey: ["cleaner-leave", cleaner.id] }); qc.invalidateQueries({ queryKey: ["cleaners"] }); qc.invalidateQueries({ queryKey: ["timesheets"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  useEffect(() => { if (!cleaner) setEditing(null); }, [cleaner]);
  const periods = data?.periods || [];
  return (
    <Dialog open={!!cleaner} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="leave-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarRange className="h-5 w-5" /> {t("Leave")} — {cleaner?.name}</DialogTitle>
          <DialogDescription>{t("Leave takes the cleaner off the roster for those dates and writes the reason onto their timesheet. Normal rostered days off are not affected.")}</DialogDescription>
        </DialogHeader>
        {editing === "new" || (editing && editing.id) ? (
          <LeaveForm cleanerId={cleaner.id} initial={editing === "new" ? null : editing} requests={data?.unlinked_requests || []} onDone={() => setEditing(null)} />
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {data?.unlinked_requests?.length ? t("{{count}} approved application(s) not yet entered", { count: data.unlinked_requests.length }) : ""}
            </span>
            <Button size="sm" onClick={() => setEditing("new")} className="gap-1" data-testid="leave-add-btn"><Plus className="h-3.5 w-3.5" /> {t("Add leave")}</Button>
          </div>
        )}
        <div className="space-y-1.5" data-testid="leave-list">
          {periods.length === 0 && <p className="text-sm italic text-muted-foreground" data-testid="leave-empty">{t("No leave recorded.")}</p>}
          {periods.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm" data-testid={`leave-row-${p.id}`}>
              <span className="font-semibold">{t(p.label_shown)}</span>
              <span className="text-muted-foreground">{fmtDMY(p.start_date)}{p.end_date !== p.start_date ? ` – ${fmtDMY(p.end_date)}` : ""}</span>
              <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-800" data-testid={`leave-days-${p.id}`}>
                {t("{{count}} leave day(s)", { count: p.rostered_days })}{p.calendar_days !== p.rostered_days ? ` · ${t("{{count}} calendar", { count: p.calendar_days })}` : ""}
              </span>
              {p.request_id && <span className="text-xs text-muted-foreground">{t("from application")}</span>}
              {p.notes && <span className="w-full text-xs text-muted-foreground sm:w-auto">{p.notes}</span>}
              <span className="ml-auto flex gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(p)} data-testid={`leave-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" disabled={remove.isPending} onClick={() => remove.mutate(p.id)} data-testid={`leave-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
